import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import type { DigitalOrderPersistenceDto } from '@/shared/api/digitalOrder';
import {
    GetDigitalOrderMethod,
    type GetDigitalOrderPathParameters,
    type GetDigitalOrderPaymentContextResponseSuccess,
    type GetDigitalOrderSearchParams,
    getGetDigitalOrderUrl,
    getUpdateDigitalOrderUrl,
    type SchemaDigitalOrder,
    UpdateDigitalOrderMethod,
    type UpdateDigitalOrderResponseSuccess,
} from '@/shared/api/openApi';
import { OrderAcquiringTypes } from '@/shared/api/order';
import { request } from '@/shared/api/request';
import { nativePayEnabled } from '@/shared/lib/helpers';
import { getRequestContext } from '@/shared/lib/requestContext';
import { OrderPaymentError } from '../errors/OrderPaymentError';
import type { UpdateDigitalOrderRequestParams } from '../types';
import { getPaymentContext } from './paymentApi';

const api = createApi({
    name: 'entities/digitalOrder/api/api',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            GetDigitalOrder: builder.query<
                SchemaDigitalOrder,
                GetDigitalOrderSearchParams & GetDigitalOrderPathParameters
            >({
                query: ({ signature, ...params }) => {
                    return {
                        url: getGetDigitalOrderUrl(params),
                        method: GetDigitalOrderMethod,
                        params: { signature },
                    };
                },
            }),

            UpdateDigitalOrder: builder.mutation<UpdateDigitalOrderResponseSuccess, UpdateDigitalOrderRequestParams>({
                query: ({ signature, storeSlug, ...payload }) => {
                    return {
                        url: getUpdateDigitalOrderUrl({ order_id: payload.orderMeta.order_id, store_slug: storeSlug }),
                        method: UpdateDigitalOrderMethod,
                        body: {
                            ...payload.orderMeta,
                            timestamp: Date.now(),
                        },
                        params: { signature },
                    };
                },
            }),
        };
    },
});

export const { useGetDigitalOrderQuery } = api;
export const getDigitalOrderQuery = api.endpoints.GetDigitalOrder.query;

export const updateDigitalOrder = (payload: UpdateDigitalOrderRequestParams) =>
    api.endpoints.UpdateDigitalOrder.mutate(payload);

async function processCloudPayment(
    options: GetDigitalOrderPaymentContextResponseSuccess['options'],
    storeUrl?: string
): Promise<boolean> {
    const widget = new window.cp.CloudPayments({});
    const paymentResponse = await widget.pay(
        'charge',
        {
            ...options,
            configuration: {
                common: {
                    successRedirectUrl: storeUrl,
                },
            },
            amount: Number(options?.amount),
        },
        {
            onSuccess: storeUrl,
        }
    );

    if (paymentResponse.status === 'success') {
        return true;
    }

    if (paymentResponse.status === 'fail') {
        throw new OrderPaymentError();
    }

    return false;
}

export async function proceedOrderPayment(
    orderPersistenceDto: DigitalOrderPersistenceDto,
    signature: string,
    storeSlug: string,
    storeUrl?: string
): Promise<boolean> {
    const {
        account,
        yandexPay: { nativePayDisabled, yandexPayAvailableForAnonymous },
    } = getRequestContext();

    const useNativePay = nativePayEnabled({
        yandexPayAvailableForAnonymous,
        nativePayDisabled,
        loggedIn: account.loggedIn,
        paymentMethod: orderPersistenceDto.payment_method,
    });

    await api.endpoints.UpdateDigitalOrder.mutate({
        orderMeta: {
            ...orderPersistenceDto,
            acquiring_type: useNativePay ? OrderAcquiringTypes.YandexPay : OrderAcquiringTypes.CloudPay,
        },
        signature,
        storeSlug,
    });

    const paymentContext = useNativePay
        ? undefined
        : await getPaymentContext({
              order_id: orderPersistenceDto.order_id,
              signature,
              store_slug: storeSlug,
              // TODO SS-5625 Убрать как бэк научится определять залогинен ли пользователь
              logged_in: account.loggedIn,
              on_success_redirect: window.location.href,
              on_error_redirect: window.location.href,
              on_abort_redirect: window.location.href,
          });

    // Оплата с помощью Яндекс Пэй или другим сервисом во внешней системе
    if (paymentContext?.url || useNativePay) {
        if (paymentContext?.acquiring_type === OrderAcquiringTypes.YandexPay && useNativePay) {
            return true;
        }

        if (paymentContext?.url) {
            window.location.href = paymentContext.url;
        }

        return true;
    }

    // Оплата с помощью CloudPayments
    return processCloudPayment(paymentContext?.options, storeUrl);
}
